import { Trans, useLingui } from '@lingui/react/macro'
import { Button, DatePicker, InputNumber, Select } from 'antd'
import { Accordion } from 'components/Accordion'
import CantileverRow from 'components/CantileverRow'
import dayjs, { Dayjs } from 'dayjs'
import { getSensorName, useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import {
  SectionContent,
  SmallText,
  SpacedSection,
} from 'features/timeSeries/sharedStyles'
import { ChevronDown, Plus, X } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry, RegressionConfig } from 'types'

const { Option } = Select

const CoefficientsTable = styled.table`
  width: 100%;
  margin-top: 0.5rem;
  font-size: 0.75rem;

  th,
  td {
    padding: 0.25rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }

  th {
    font-weight: 600;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const ExpandButton = styled(Button)`
  margin-top: 0.5rem;
  width: 100%;

  .ant-btn-icon {
    transition: transform 0.3s;
  }

  &[data-expanded='true'] .ant-btn-icon {
    transform: rotate(180deg);
  }
`

const TruncatedText = styled.span`
  display: inline-block;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`

interface RegressionPredictionProps {
  selectedSensors: BaseSensorEntry[]
  sensorEntries: BaseSensorEntry[]
}

export const RegressionPrediction: React.FC<RegressionPredictionProps> = ({
  selectedSensors,
  sensorEntries,
}) => {
  const { t } = useLingui()

  const {
    derivedValues,
    setDerivedValues,
    regressionConfigs,
    setRegressionConfigs,
    regressionResults,
    setRegressionResults,
  } = useScatterplotContext()
  const DEFAULT_REGRESSION: Partial<RegressionConfig> = {
    xVariables: ['timestamp'],
    regressionType: 'linear',
    enabled: true,
    endDate: dayjs().add(30, 'days').format('YYYY-MM-DD'),
  }
  const [showRegressionForm, setShowRegressionForm] = useState(false)
  const [newRegression, setNewRegression] = useState<Partial<RegressionConfig>>(
    {
      ...DEFAULT_REGRESSION,
    }
  )

  const [expandedResults, setExpandedResults] = useState<
    Record<string, boolean>
  >({})

  return (
    <SectionContent>
      <SmallText type='secondary'>
        <Trans>Fit regression models and predict future values</Trans>
      </SmallText>

      {selectedSensors.length > 0 ? (
        <SpacedSection>
          {regressionConfigs.length > 0 && (
            <Accordion
              defaultValue={[]}
              items={regressionConfigs.map((config) => {
                const sensor = selectedSensors.find(
                  (s) => s.id === config.yVariable
                )
                const derivedValue = derivedValues.find(
                  (dv) => dv.id === config.yVariable
                )
                const sensorName = sensor
                  ? getSensorName(sensor.id, derivedValues, sensorEntries)
                  : derivedValue?.data.name || config.yVariable

                return {
                  title: (
                    <CantileverRow
                      leadingContent={
                        <ChartTooltip label={`${sensorName} - ${config.type}`}>
                          <TruncatedText>
                            {sensorName} - {config.type}
                          </TruncatedText>
                        </ChartTooltip>
                      }
                      trailingContent={
                        <CantileverRow>
                          <Button
                            onClick={() => {
                              setRegressionConfigs((prev) =>
                                prev.map((c) =>
                                  c.id === config.id
                                    ? {
                                        ...c,
                                        shouldCalculate: true,
                                      }
                                    : c
                                )
                              )
                            }}
                            disabled={config.shouldCalculate ?? false}>
                            <Trans>Recalc</Trans>
                          </Button>
                          <Button
                            danger
                            onClick={() => {
                              setRegressionConfigs((prev) =>
                                prev.filter((c) => c.id !== config.id)
                              )
                              setRegressionResults((prev) => {
                                const newResults = { ...prev }
                                delete newResults[config.id]
                                return newResults
                              })

                              // Also remove the associated derived value
                              const derivedValueId = `regression_predicted_${config.id}`
                              setDerivedValues((prev) =>
                                prev.filter((dv) => dv.id !== derivedValueId)
                              )
                            }}>
                            <X size={12} />
                          </Button>
                        </CantileverRow>
                      }
                    />
                  ),
                  key: config.id,
                  disabled: !regressionResults[config.id],
                  content: regressionResults[config.id] && (
                    <div>
                      <CantileverRow
                        leadingContent={
                          <SmallText>
                            <Trans>R² =</Trans>{' '}
                            {regressionResults[config.id]?.r2?.toFixed(4) ||
                              t`N/A`}
                          </SmallText>
                        }
                        trailingContent={
                          <SmallText type={'secondary'}>
                            {regressionResults[config.id].featureNames
                              ?.length || 0}{' '}
                            feature
                            {(regressionResults[config.id].featureNames
                              ?.length ?? 0) > 1
                              ? 's'
                              : ''}
                          </SmallText>
                        }
                      />
                      <CantileverRow>
                        <SmallText>
                          {regressionResults[config.id].equation}
                        </SmallText>
                      </CantileverRow>
                      <ExpandButton
                        type='text'
                        data-expanded={expandedResults[config.id]}
                        onClick={() =>
                          setExpandedResults((prev) => ({
                            ...prev,
                            [config.id]: !prev[config.id],
                          }))
                        }
                        icon={<ChevronDown size={14} />}>
                        {expandedResults[config.id] ? t`Hide` : t`Show`}
                        <Trans> Details</Trans>
                      </ExpandButton>
                      {expandedResults[config.id] && (
                        <CoefficientsTable>
                          <thead>
                            <tr>
                              <th>
                                <Trans>Variable</Trans>
                              </th>
                              <th>
                                <Trans>Coefficient</Trans>
                              </th>
                              <th>
                                <Trans>P-Val</Trans>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <Trans>Intercept</Trans>
                              </td>
                              <td>
                                {regressionResults[
                                  config.id
                                ].coefficients[0]?.toPrecision(4) || '0'}
                              </td>
                              <td>
                                {regressionResults[
                                  config.id
                                ].pVals?.[0]?.toPrecision(4) || '0'}
                              </td>
                            </tr>
                            {regressionResults[config.id].featureNames?.map(
                              (name: string, i: number) => (
                                <tr key={i}>
                                  <td>{name}</td>
                                  <td>
                                    {regressionResults[config.id].coefficients[
                                      i + 1
                                    ]?.toPrecision(4) || '0'}
                                  </td>
                                  <td>
                                    {regressionResults[config.id].pVals?.[
                                      i + 1
                                    ]?.toPrecision(4) || '0'}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </CoefficientsTable>
                      )}
                    </div>
                  ),
                }
              })}
            />
          )}

          {!showRegressionForm && (
            <Button
              type='dashed'
              onClick={() => setShowRegressionForm(true)}
              block
              icon={<Plus size={14} />}>
              <Trans>Add Regression</Trans>
            </Button>
          )}

          {showRegressionForm && (
            <div>
              {newRegression.regressionType === 'linear' && (
                <CantileverRow
                  leadingContent={
                    <SmallText>
                      <Trans>Independent Variable(s) (X)</Trans>
                    </SmallText>
                  }
                  trailingContent={
                    <Select
                      mode='multiple'
                      value={newRegression.xVariables}
                      onChange={(values) =>
                        setNewRegression({
                          ...newRegression,
                          xVariables: values as string[],
                        })
                      }
                      style={{ minWidth: '9rem', maxWidth: '20rem' }}
                      placeholder='Select X variable(s)'
                      maxTagCount={2}>
                      <Option value='timestamp'>Time</Option>
                      <Select.OptGroup label='Sensors'>
                        {selectedSensors
                          .filter(
                            (sensor) => sensor.id !== newRegression.yVariable
                          )
                          .map((sensor) => (
                            <Option key={sensor.id} value={sensor.id}>
                              {getSensorName(
                                sensor.id,
                                derivedValues,
                                sensorEntries
                              )}
                            </Option>
                          ))}
                      </Select.OptGroup>
                      {derivedValues.length > 0 && (
                        <Select.OptGroup label='Derived Values'>
                          {derivedValues
                            .filter((dv) => {
                              return (
                                dv.id !== newRegression.yVariable &&
                                dv.data.dependencyIds.every((sensorId) =>
                                  selectedSensors.some((s) => s.id === sensorId)
                                )
                              )
                            })
                            .map((dv) => (
                              <Option key={dv.id} value={dv.id}>
                                {dv.data.name}
                              </Option>
                            ))}
                        </Select.OptGroup>
                      )}
                    </Select>
                  }
                />
              )}
              <CantileverRow
                leadingContent={
                  <SmallText>
                    <Trans>Dependent Variable (Y)</Trans>
                  </SmallText>
                }
                trailingContent={
                  <Select
                    value={newRegression.yVariable}
                    onChange={(value) =>
                      setNewRegression({
                        ...newRegression,
                        yVariable: value as string,
                      })
                    }
                    placeholder='Select Y variable'>
                    <Select.OptGroup label='Sensors'>
                      {selectedSensors.map((sensor) => (
                        <Option key={sensor.id} value={sensor.id}>
                          <ChartTooltip
                            side='right'
                            label={getSensorName(
                              sensor.id,
                              derivedValues,
                              sensorEntries
                            )}>
                            <TruncatedText>
                              {getSensorName(
                                sensor.id,
                                derivedValues,
                                sensorEntries
                              )}
                            </TruncatedText>
                          </ChartTooltip>
                        </Option>
                      ))}
                    </Select.OptGroup>
                    {derivedValues.length > 0 && (
                      <Select.OptGroup label='Derived Values'>
                        {derivedValues
                          .filter((dv) => {
                            return dv.data.dependencyIds.every((sensorId) =>
                              selectedSensors.some((s) => s.id === sensorId)
                            )
                          })
                          .map((dv) => (
                            <Option key={dv.id} value={dv.id}>
                              {dv.data.name}
                            </Option>
                          ))}
                      </Select.OptGroup>
                    )}
                  </Select>
                }
              />

              <CantileverRow
                leadingContent={
                  <SmallText>
                    <Trans>Model type</Trans>
                  </SmallText>
                }
                trailingContent={
                  <Select
                    value={newRegression.regressionType}
                    onChange={(value) => {
                      const updates: Partial<RegressionConfig> = {
                        type: value as RegressionConfig['type'],
                      }
                      if (value !== 'polynomial') {
                        updates.degree = undefined
                      }
                      setNewRegression({ ...newRegression, ...updates })
                    }}>
                    <Option value='linear'>Linear</Option>
                    <Option value='polynomial'>Polynomial</Option>
                    <Option value='logistic'>Logistic</Option>
                    <Option value='sinusoidal'>Sinusoidal</Option>
                  </Select>
                }
              />

              {newRegression.regressionType === 'polynomial' && (
                <CantileverRow
                  leadingContent={
                    <SmallText>
                      <Trans>Degree</Trans>
                    </SmallText>
                  }
                  trailingContent={
                    <InputNumber
                      min={2}
                      max={5}
                      value={newRegression.degree || 2}
                      onChange={(value) =>
                        setNewRegression({
                          ...newRegression,
                          degree: typeof value === 'number' ? value : 2,
                        })
                      }
                    />
                  }
                />
              )}

              {(newRegression.regressionType !== 'linear' ||
                newRegression.xVariables?.includes('timestamp')) && (
                <CantileverRow
                  leadingContent={
                    <SmallText>
                      <Trans>Extend to date</Trans>
                    </SmallText>
                  }
                  trailingContent={
                    <DatePicker
                      value={
                        newRegression.endDate
                          ? dayjs(newRegression.endDate)
                          : null
                      }
                      onChange={(date: Dayjs | null) =>
                        setNewRegression({
                          ...newRegression,
                          endDate:
                            date?.format('YYYY-MM-DD') ||
                            dayjs().add(30, 'days').format('YYYY-MM-DD'),
                        })
                      }
                      format='YYYY-MM-DD'
                      placeholder={t`Select end date`}
                    />
                  }
                />
              )}

              <CantileverRow
                tPadding='0.5rem'
                leadingContent={
                  <Button
                    onClick={() => {
                      setShowRegressionForm(false)
                      setNewRegression({
                        ...DEFAULT_REGRESSION,
                      })
                    }}>
                    <Trans>Cancel</Trans>
                  </Button>
                }
                trailingContent={
                  <Button
                    type='primary'
                    onClick={() => {
                      if (!newRegression.yVariable) return

                      const config: RegressionConfig = {
                        type: 'regression',
                        id: `regression_${Date.now()}`,
                        enabled: true,
                        yVariable: newRegression.yVariable,
                        xVariables:
                          newRegression.xVariables ||
                          (newRegression.regressionType === 'linear'
                            ? []
                            : ['timestamp']),
                        regressionType:
                          newRegression.regressionType || 'linear',
                        degree: newRegression.degree,
                        endDate:
                          newRegression.endDate ||
                          dayjs().add(30, 'days').format('YYYY-MM-DD'),
                        minX: undefined,
                        maxX: undefined,
                        shouldCalculate: true,
                      }

                      setRegressionConfigs((prev) => [...prev, config])
                      setShowRegressionForm(false)
                      setNewRegression({
                        ...DEFAULT_REGRESSION,
                      })
                    }}
                    disabled={!newRegression.yVariable}>
                    <Trans>Add Regression</Trans>
                  </Button>
                }
              />
            </div>
          )}
        </SpacedSection>
      ) : (
        <SmallText type='secondary'>
          <Trans>No sensors selected</Trans>
        </SmallText>
      )}
    </SectionContent>
  )
}
